/**
 * ENG-5445: read Claude Code's `~/.claude/stats-cache.json` and forward
 * per-day activity counts to the API. Complements the usage-banner
 * monitor (ENG-5389) — the passive banner only renders at the weekly-
 * limit threshold (~75%+), so quietly-busy hosts under that bar are
 * invisible to the banner path. stats-cache.json is host-wide and
 * captures every session on the machine, giving us volume signal
 * regardless of usage percentage.
 *
 * Host-scoped, not agent-scoped: stats-cache.json aggregates across
 * every Claude Code session on the host, so this module runs once per
 * refresh cycle (not per-agent). The companion table is
 * `claude_code_activity_observations` (ENG-5446), keyed on
 * (host_id, date).
 *
 * "Only new days" semantics:
 *   - Keep a single in-memory `lastObservedDate` for the host.
 *   - On each call, read the cache file, and POST any `dailyActivity`
 *     row whose `date` is strictly greater than `lastObservedDate`.
 *   - The endpoint's upsert-on-(host_id, date) makes the contract
 *     idempotent — re-posting the same day is harmless — but the
 *     in-memory gate keeps write volume low at steady state.
 *
 * Rate-limited to MIN_CHECK_INTERVAL_MS so a fast manager poll doesn't
 * translate into N file reads per second on the host.
 *
 * Errors are soft. A missing / unreadable cache, a parser failure, or
 * a 4xx/5xx from the API must NOT break the manager poll cycle — log
 * and move on. The schema-level uniqueness guarantees we'll catch up
 * the next time the file is readable.
 *
 * Probe-feasibility note for the spec-curious reader: the ENG-5443
 * spike attempted a dedicated `/usage` probe session to capture the
 * always-on percentage (vs ENG-5389's threshold-gated banner). That
 * primary path remains unproven — `tmux send-keys` is dropped by the
 * TUI ("paste-not-submitting"), `claude -p "/usage"` doesn't expand
 * slash commands, and the positional-arg path is blocked by the
 * workspace-trust dialog. This module ships the spike's secondary
 * path (stats-cache.json) on its own.
 */

import { existsSync, readFileSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';

const MIN_CHECK_INTERVAL_MS = 60_000;
const STATS_CACHE_PATH = join(homedir(), '.claude', 'stats-cache.json');
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Shape of `~/.claude/stats-cache.json` that this module cares about.
 * Claude Code includes more fields (totalMessages, modelUsage,
 * hourCounts, etc.); we read only what we forward.
 */
interface StatsCacheFile {
  dailyActivity?: Array<{
    date?: unknown;
    messageCount?: unknown;
    sessionCount?: unknown;
    toolCallCount?: unknown;
  }>;
  dailyModelTokens?: Record<string, Record<string, unknown>>;
  lastComputedDate?: unknown;
}

interface MonitorState {
  /** Last YYYY-MM-DD we posted (or null if never). */
  lastObservedDate: string | null;
  /** Last time we actually opened the cache file. */
  lastCheckedAt: number;
}

const state: MonitorState = { lastObservedDate: null, lastCheckedAt: 0 };

/** Test helper — reset module state. Not for production use. */
export function _resetActivityCacheMonitorState(): void {
  state.lastObservedDate = null;
  state.lastCheckedAt = 0;
}

/** Minimal API surface — lets tests stub a transport. */
export interface ActivityCacheMonitorApi {
  post(path: string, body: unknown): Promise<unknown>;
}

/**
 * Pure helper for tests. Parses the raw file contents and returns the
 * sorted-ascending list of day-rows we'd POST given `lastObservedDate`.
 * Tolerates missing / malformed fields per the file's documented
 * lazy-update behaviour.
 */
export function selectNewDailyRows(
  raw: string,
  lastObservedDate: string | null,
): Array<{
  date: string;
  message_count: number;
  session_count: number;
  tool_call_count: number;
  model_breakdown: Record<string, unknown> | null;
}> {
  let parsed: StatsCacheFile;
  try {
    parsed = JSON.parse(raw) as StatsCacheFile;
  } catch {
    return [];
  }
  const days = Array.isArray(parsed.dailyActivity) ? parsed.dailyActivity : [];

  const nonNegInt = (v: unknown): number | null => {
    if (typeof v !== 'number' || !Number.isFinite(v)) return null;
    const n = Math.trunc(v);
    return n >= 0 ? n : null;
  };

  const result: Array<{
    date: string;
    message_count: number;
    session_count: number;
    tool_call_count: number;
    model_breakdown: Record<string, unknown> | null;
  }> = [];

  for (const d of days) {
    if (typeof d.date !== 'string' || !ISO_DATE_RE.test(d.date)) continue;
    if (lastObservedDate !== null && d.date <= lastObservedDate) continue;
    const m = nonNegInt(d.messageCount);
    const s = nonNegInt(d.sessionCount);
    const t = nonNegInt(d.toolCallCount);
    if (m === null || s === null || t === null) continue;

    // Per-day model breakdown lives in a sibling field (`dailyModelTokens`),
    // keyed by date string. Pass it through verbatim when present; the API
    // accepts `model_breakdown` as an arbitrary jsonb object.
    const breakdownForDay = parsed.dailyModelTokens?.[d.date];
    const model_breakdown =
      breakdownForDay && typeof breakdownForDay === 'object' && !Array.isArray(breakdownForDay)
        ? (breakdownForDay as Record<string, unknown>)
        : null;

    result.push({
      date: d.date,
      message_count: m,
      session_count: s,
      tool_call_count: t,
      model_breakdown,
    });
  }

  // Ascending by date so the in-memory `lastObservedDate` advances
  // monotonically even if the API call for an earlier day fails.
  result.sort((a, b) => a.date.localeCompare(b.date));
  return result;
}

/**
 * Read the stats cache, POST any day-rows newer than the in-memory
 * `lastObservedDate`, advance the marker on successful writes.
 *
 * Soft on every error path — this is observability, not the critical
 * path. A persistent failure shows up as stale rows in
 * `claude_code_activity_observations` (which the dashboard can flag),
 * not as a manager outage.
 */
export async function maybeReportActivityCache(args: {
  api: ActivityCacheMonitorApi;
  log: (msg: string) => void;
  now?: Date;
}): Promise<void> {
  const { api, log } = args;
  const now = args.now ?? new Date();
  const nowMs = now.getTime();

  if (nowMs - state.lastCheckedAt < MIN_CHECK_INTERVAL_MS) return;
  state.lastCheckedAt = nowMs;

  if (!existsSync(STATS_CACHE_PATH)) {
    // Expected on hosts that have never run Claude Code (e.g. brand-new
    // managers waiting on first agent spawn). Quietly bail.
    return;
  }

  let raw: string;
  try {
    raw = readFileSync(STATS_CACHE_PATH, 'utf-8');
  } catch (err) {
    log(`[activity-cache] readFileSync failed: ${(err as Error).message}`);
    return;
  }

  const rows = selectNewDailyRows(raw, state.lastObservedDate);
  if (rows.length === 0) return;

  for (const row of rows) {
    try {
      await api.post('/host/activity-observations', row);
      // Advance the marker only when the POST landed. If a later row
      // in the same batch fails, retry-on-next-tick covers it.
      state.lastObservedDate = row.date;
    } catch (err) {
      log(
        `[activity-cache] POST /host/activity-observations failed for date=${row.date}: ${(err as Error).message}`,
      );
      // Don't advance the marker — next tick retries this row + the
      // ones after it. Stop the batch so we don't paper over a
      // systemic failure with a flood of misordered writes.
      return;
    }
  }
}
